import type {
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'

import { cancelAndInvalidate } from './common'
import * as TripDocumentsKeys from '../reactQueryKeys/tripDocuments'
import * as TripKeys from '../reactQueryKeys/trips'

import { trackClientEvent } from 'analytics/analytics'
import { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import {
  attachTripDocumentToTripItem,
  deleteTripDocument,
  detachTripDocumentToTripItem,
  getTripDocumentURL,
  getTripDocuments,
  renameAttachedTripDocument,
  renameUnattachedTripDocument,
  uploadTripDocument,
} from 'tripPlanner/api/documents'
import type {
  DeleteTripDocumentError,
  DeleteTripDocumentResponse,
  UpdateUnattachedDocumentError,
  UpdateUnattachedDocumentResponse,
  UpdateAttachedDocumentError,
  UpdateAttachedDocumentResponse,
  GetTripDocumentsError,
  GetTripDocumentsResponse,
  GetTripDocumentsURLError,
  UploadTripDocumentError,
  UploadTripDocumentResponse,
} from 'tripPlanner/api/documents/types'
import * as TripDocumentsMapper from 'tripPlanner/mappers/documents'
import type { FullTrip } from 'tripPlanner/types/common'
import type { TripDocument } from 'tripPlanner/types/documents'

const MINUTES = 60 * 1000
const TRIP_DOCUMENTS_STALE_TIME = 4 * MINUTES
const TRIP_PRESIGNED_URL_STALE_TIME = 15 * MINUTES

export interface TripDocumentsResult {
  attachedFiles: { [key: string]: Array<TripDocument> }
  unAttachedFiles: Array<TripDocument>
}

export type TripDocumentURL = string

export const useTripDocuments = ({
  tripId,
  ...options
}: {
  tripId?: string
  withPlaceholder?: boolean
} & UseQueryOptions<
  GetTripDocumentsResponse['result'],
  GetTripDocumentsError,
  TripDocumentsResult,
  ReturnType<typeof TripDocumentsKeys.forTrip>
>): UseQueryResult<TripDocumentsResult, GetTripDocumentsError> => {
  const queryClient = useQueryClient()
  const trip = queryClient.getQueryData<FullTrip | undefined>(
    TripKeys.detail(tripId!),
  )
  return useQuery(
    TripDocumentsKeys.forTrip(tripId!),
    () => getTripDocuments({ tripId: tripId! }),
    {
      enabled: !!trip,
      staleTime: TRIP_DOCUMENTS_STALE_TIME,
      select: TripDocumentsMapper.mapTripDocuments,
      ...options,
    },
  )
}

export const useTripDocumentUrl = ({
  documentKey,
  ...options
}: {
  documentKey: string
}): UseQueryResult<TripDocumentURL, GetTripDocumentsURLError> => {
  return useQuery(
    TripDocumentsKeys.forURL(documentKey),
    () => getTripDocumentURL({ documentKey }),
    {
      enabled: !!documentKey,
      staleTime: TRIP_PRESIGNED_URL_STALE_TIME,
      select: (data) => data.presignedUrl,
      ...options,
    },
  )
}

interface DocumentUploadOptions
  extends UseMutationOptions<
    UploadTripDocumentResponse['result'],
    UploadTripDocumentError,
    File,
    void
  > {
  tripId: string
  tripItemId?: string
}

export const useUploadDocument = (
  options: DocumentUploadOptions,
): UseMutationResult<
  UploadTripDocumentResponse['result'],
  UploadTripDocumentError,
  File
> & {
  progress: number
  retryUpload: (options?: DocumentUploadOptions | undefined) => void
} => {
  const queryClient = useQueryClient()
  const attemptedFile = useRef<File | null>(null)

  const { tripId, tripItemId, ...restOptions } = options

  const [progress, setProgress] = useState(0)

  const result = useMutation(
    (file) =>
      uploadTripDocument({
        tripId,
        tripItemId,
        file,
        onProgress: setProgress,
      }),
    {
      retry: false,
      ...restOptions,
      onMutate: (file) => {
        attemptedFile.current = file
        setProgress(0)
        options.onMutate?.(file)
      },
      onSuccess: (data, variables, context) => {
        trackClientEvent({
          action: 'upload',
          subject: 'trip_document',
          category: AnalyticsPage.tripPlanner,
          type: 'interaction',
        })

        // Update the trip documents query
        queryClient.setQueryData(
          TripDocumentsKeys.forTrip(tripId),
          (oldData: GetTripDocumentsResponse['result'] | undefined) => {
            if (!oldData) {
              return oldData
            }
            if (tripItemId) {
              const newAttachedFiles = {
                ...oldData.attachedFiles,
                [tripItemId]: [
                  ...(oldData.attachedFiles[tripItemId] ?? []),
                  data,
                ],
              }
              return {
                ...oldData,
                attachedFiles: newAttachedFiles,
              }
            } else {
              return {
                ...oldData,
                unAttachedFiles: [...oldData.unAttachedFiles, data],
              }
            }
          },
        )
        options.onSuccess?.(data, variables, context)
      },
    },
  )

  const retryUpload = useCallback(
    (options?: DocumentUploadOptions | undefined) => {
      if (attemptedFile.current && result.isError) {
        result.mutate(attemptedFile.current, options)
      }
    },
    [result],
  )

  return {
    ...result,
    progress,
    retryUpload,
  }
}

export const useDeleteDocument = (
  options: UseMutationOptions<
    DeleteTripDocumentResponse['result'],
    DeleteTripDocumentError,
    Parameters<typeof deleteTripDocument>[0]
  > & { tripId: string },
): UseMutationResult<
  DeleteTripDocumentResponse['result'],
  DeleteTripDocumentError,
  Parameters<typeof deleteTripDocument>[0]
> => {
  const queryClient = useQueryClient()

  return useMutation((params) => deleteTripDocument(params), {
    retry: false,
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(
        queryClient,
        TripDocumentsKeys.forTrip(options.tripId),
      )
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

export const useAttachDocument = (
  options: UseMutationOptions<
    UpdateUnattachedDocumentResponse['result'],
    UpdateUnattachedDocumentError,
    Parameters<typeof attachTripDocumentToTripItem>[0]
  > & { tripId: string },
): UseMutationResult<
  UpdateUnattachedDocumentResponse['result'],
  UpdateUnattachedDocumentError,
  Parameters<typeof attachTripDocumentToTripItem>[0]
> => {
  const queryClient = useQueryClient()

  return useMutation((params) => attachTripDocumentToTripItem(params), {
    retry: false,
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(
        queryClient,
        TripDocumentsKeys.forTrip(options.tripId),
      )
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

export const useDetachDocument = (
  options: UseMutationOptions<
    UpdateAttachedDocumentResponse['result'],
    UpdateAttachedDocumentError,
    Parameters<typeof detachTripDocumentToTripItem>[0]
  > & { tripId: string },
): UseMutationResult<
  UpdateAttachedDocumentResponse['result'],
  UpdateAttachedDocumentError,
  Parameters<typeof detachTripDocumentToTripItem>[0]
> => {
  const queryClient = useQueryClient()

  return useMutation((params) => detachTripDocumentToTripItem(params), {
    retry: false,
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(
        queryClient,
        TripDocumentsKeys.forTrip(options.tripId),
      )
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

export const useRenameAttachedDocument = (
  options: UseMutationOptions<
    UpdateAttachedDocumentResponse['result'],
    UpdateAttachedDocumentError,
    Parameters<typeof renameAttachedTripDocument>[0]
  > & { tripId: string },
): UseMutationResult<
  UpdateAttachedDocumentResponse['result'],
  UpdateAttachedDocumentError,
  Parameters<typeof renameAttachedTripDocument>[0]
> => {
  const queryClient = useQueryClient()

  return useMutation((params) => renameAttachedTripDocument(params), {
    retry: false,
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(
        queryClient,
        TripDocumentsKeys.forTrip(options.tripId),
      )
      options?.onSettled?.(data, error, variables, context)
    },
  })
}

export const useRenameUnattachedDocument = (
  options: UseMutationOptions<
    UpdateUnattachedDocumentResponse['result'],
    UpdateUnattachedDocumentError,
    Parameters<typeof renameUnattachedTripDocument>[0]
  > & { tripId: string },
): UseMutationResult<
  UpdateUnattachedDocumentResponse['result'],
  UpdateUnattachedDocumentError,
  Parameters<typeof renameUnattachedTripDocument>[0]
> => {
  const queryClient = useQueryClient()

  return useMutation((params) => renameUnattachedTripDocument(params), {
    retry: false,
    ...options,
    onSettled: (data, error, variables, context) => {
      cancelAndInvalidate(
        queryClient,
        TripDocumentsKeys.forTrip(options.tripId),
      )
      options?.onSettled?.(data, error, variables, context)
    },
  })
}
